import React, { useState } from 'react';
import { EmptyText, TablePagination } from 'library/ui';
import ChangeModal from 'parent-portal/subs/ConfirmSubHours/ChangeModal';
import { ClockInModal } from 'parent-portal/jobs/upcoming/BusinessCalendar/ShiftCard/ClockInButton';

import { SmallTimesheetsTable } from './SmallTimesheetsTable';
import { LargeTimesheetsTable } from './LargeTimesheetsTable';
import { TimesheetOptionsModal } from './TimesheetOptionsModal';

import type { IHourReport } from 'internal/payments/HourReportModels';
import type { CenterMissingPayment, PagingResult } from 'models';
import type { TimesheetWorker, UnclockedInTimesheetWorker } from 'parent-portal/home/types';
import { MissingPaymentApprovalModal } from './MissingPaymentApprovalModal';
import { useMediaQuery } from '@mui/material';
import theme from 'theme';

export function TimesheetsTable({
    data,
    loading,
    refresh,
    page,
    setPage,
}: {
    data?:
        | PagingResult<TimesheetWorker>
        | PagingResult<UnclockedInTimesheetWorker>
        | PagingResult<CenterMissingPayment>;
    loading: boolean;
    refresh: () => void;
    page: number;
    setPage: (page: number) => void;
}) {
    const [changeReport, setChangeReport] = useState<IHourReport | undefined>(undefined);
    const [clockInWorker, setClockInWorker] = useState<TimesheetWorker | UnclockedInTimesheetWorker | undefined>(
        undefined,
    );
    const [finalChangeReport, setFinalChangeReport] = useState<IHourReport | undefined>(undefined);
    const [missingPayment, setMissingPayment] = useState<CenterMissingPayment | undefined>(undefined);
    const isSmall = useMediaQuery(theme.breakpoints.down('md'));

    function openModal(timesheet: TimesheetWorker | UnclockedInTimesheetWorker | CenterMissingPayment) {
        if (!('hour_report' in timesheet)) {
            setMissingPayment(timesheet);
            return;
        }

        if (timesheet.hour_report?.start && timesheet.hour_report?.end) {
            setFinalChangeReport({
                ...timesheet.hour_report,
                timezone: timesheet.timezone,
            });
        } else if (timesheet.hour_report) {
            setChangeReport({
                ...timesheet.hour_report,
                timezone: timesheet.timezone,
            });
        } else {
            setClockInWorker(timesheet);
        }
    }

    if (loading || !data) return <EmptyText>Loading...</EmptyText>;

    if (!data.results.length) {
        return <EmptyText>No timesheets found.</EmptyText>;
    }

    return (
        <div>
            {isSmall ? (
                <SmallTimesheetsTable data={data.results} editAction={openModal} />
            ) : (
                <LargeTimesheetsTable data={data.results} editAction={openModal} />
            )}
            {changeReport && (
                <ChangeModal
                    changeReport={changeReport}
                    setChangeReport={setChangeReport}
                    updateHoursReported={refresh}
                />
            )}
            {finalChangeReport && (
                <TimesheetOptionsModal
                    isOpen={!!finalChangeReport}
                    onClose={() => setFinalChangeReport(undefined)}
                    onComplete={refresh}
                    report={finalChangeReport}
                />
            )}
            {clockInWorker && 'appointment_id' in clockInWorker && (
                <ClockInModal
                    open={!!clockInWorker}
                    onClose={() => setClockInWorker(undefined)}
                    onComplete={refresh}
                    appointmentId={clockInWorker.appointment_id}
                    startDate={clockInWorker.start_date}
                    providerName={`${clockInWorker.user.first_name} ${clockInWorker.user.last_name}`}
                    providerUserId={clockInWorker.user.id}
                    timezone={clockInWorker.timezone}
                />
            )}
            {missingPayment && (
                <MissingPaymentApprovalModal
                    missingPayment={missingPayment}
                    onClose={() => {
                        setMissingPayment(undefined);
                        refresh();
                    }}
                    open={!!missingPayment}
                />
            )}
            <TablePagination count={data.count} perPage={data.per_page} page={page} onPageChange={setPage} />
        </div>
    );
}
